import type { ReactNode } from 'react'
import React, { useEffect, useRef } from 'react'
import { BottomDrawer, Button, Modal } from '@systemeio/components'
import { Save01Icon, XCloseIcon } from '@systemeio/icons'
import type { Emoji } from 'emoji-mart'
import { useTranslation } from 'next-i18next'
import type { SubmitHandler, UseFormSetError } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import { COMMUNITY_TITLE_MAX_LENGTH, EMBEDDED_VIDEO_URL_MAX_LENGTH } from 'shared/constants'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { useIsMobileWhileOpen } from 'shared/hooks/use-is-mobile-while-open'
import { usePostAttachmentsContext } from 'modules/attachments/components/context'
import { hasEmbeddedVideoAttachmentUrlTooLong } from 'modules/attachments/utils/attachment-utils'
import { useUpdatePost } from 'modules/community/api/update-post'
import PostAttachments from 'modules/community/components/manage-post/components/attachments'
import type { EditorRef } from 'modules/community/components/manage-post/components/content'
import AddPostContentEditor from 'modules/community/components/manage-post/components/content'
import AddPostInputField from 'modules/community/components/manage-post/components/input-field'
import AddPostTopicSelect from 'modules/community/components/manage-post/components/topic-select'
import { generatePathFromName } from 'modules/community/hooks/path-utils'
import type { PostAttachmentType } from 'modules/community/types/post-attachment'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'
import { useCommunityWithTopics } from '../../hooks/use-community-with-topics'
import type { PostInterface } from '../../types/post-interface'

interface FormValues {
  title: string
  path: string
  content: string
  topic?: number
  attachments?: PostAttachmentType[]
}

type ErrorFields = Parameters<UseFormSetError<FormValues>>[0]

const errorFields: ErrorFields[] = ['title', 'path', 'topic', 'content', 'root']

export interface UpdatePostProps {
  post: PostInterface
  open: boolean
  onClose: () => void
}

export const PostUpdateModal = ({ post, open, onClose }: UpdatePostProps): ReactNode => {
  const editorRef = useRef<EditorRef>(null)
  const { t } = useTranslation()
  const isMobile = useIsMobileWhileOpen(open)

  const { data: community, isValidating } = useCommunityWithTopics()
  const { isUploading } = usePostAttachmentsContext()

  const updatePostMutation = useUpdatePost({ postId: post.id })

  const formMethods = useForm<FormValues>({
    defaultValues: {
      title: post.title,
      path: post.path,
      content: post.content,
      topic: post.topic.id,
      attachments: post.attachments,
    },
  })

  function convertNameToPath(e: React.FocusEvent<HTMLInputElement>): void {
    if (!e.target.value) {
      formMethods.setValue('path', generatePathFromName(formMethods.getValues('title')))
    }
  }

  const handleAddEmoji = (emoji: typeof Emoji.Props): void =>
    editorRef.current?.insertEmoji(emoji.native)

  const onSubmit: SubmitHandler<FormValues> = formData => {
    if (!community) return

    formMethods.clearErrors()
    const { title, path, content, topic, attachments } = formData

    if (hasEmbeddedVideoAttachmentUrlTooLong(attachments)) {
      formMethods.setError('root', {
        message: t('post.attachments.embedded_video_link.error_too_long', {
          max: EMBEDDED_VIDEO_URL_MAX_LENGTH,
        }),
      })
      return
    }

    updatePostMutation.mutate(
      {
        title,
        path,
        content,
        topic: topic ?? null,
        attachments: escapeAttachmentLocalFields(attachments),
      },
      {
        onSuccess: () => {
          onClose()
          formMethods.reset()
          editorRef.current?.clear()
        },
        onError: error => {
          if (error instanceof BadRequest) {
            errorFields.forEach(name => {
              const fieldError = error.errors?.fields?.[name]
              if (fieldError) {
                formMethods.setError(name, { message: fieldError.join('\n') })
              }

              if (name === 'root' && error.errors?.common) {
                formMethods.setError(name, { message: error.errors.common.join('\n') })
              }
            })
          } else if (error instanceof InternalError) {
            formMethods.setError('root', { message: t('core.error.internal_error_message') })
          }
        },
      },
    )
  }

  useEffect(() => {
    if (!open) {
      formMethods.reset()
      editorRef.current?.clear()
      formMethods.clearErrors()
    }
  }, [open])

  if (!community || isValidating) {
    return <div className="mb-10 h-28 animate-pulse rounded-lg bg-bg-tertiary" />
  }

  const title = t('settings.update_post.modal.title')

  const fields = (
    <>
      <AddPostInputField
        name="title"
        maxLength={COMMUNITY_TITLE_MAX_LENGTH}
        label={t('home.add_post.title')}
      />
      <AddPostInputField
        name="path"
        maxLength={32}
        onFocus={convertNameToPath}
        label={t('home.add_post.path')}
      />
      <AddPostTopicSelect />
      <AddPostContentEditor ref={editorRef} content={post.content} mentions={post.mentions} />
      <PostAttachments onAddEmoji={handleAddEmoji} />
    </>
  )

  const action = (
    <Button
      variant="primary"
      className="ml-auto"
      type="submit"
      isLoading={updatePostMutation.isPending}
      disabled={isUploading}
      data-testid="save-post-button"
    >
      <Save01Icon className="size-5" />
      {t('global.save')}
    </Button>
  )

  const rootError = (
    <div>
      {formMethods.formState.errors.root?.message && (
        <p className="mt-2 text-sm text-text-error">{formMethods.formState.errors.root.message}</p>
      )}
    </div>
  )

  return isMobile ? (
    <FormProvider {...formMethods}>
      <BottomDrawer.Root
        open={open}
        onOpenChange={value => {
          if (!value) onClose()
        }}
      >
        <BottomDrawer.Popup render={<form onSubmit={formMethods.handleSubmit(onSubmit)} />}>
          <BottomDrawer.Header className="flex-row items-start gap-2.5">
            <BottomDrawer.Title className="min-w-0 flex-1">{title}</BottomDrawer.Title>
            <BottomDrawer.Close
              aria-label="Close"
              className="shrink-0"
              render={<Button variant="ghost-gray" size="icon-sm" />}
            >
              <XCloseIcon />
            </BottomDrawer.Close>
          </BottomDrawer.Header>
          <BottomDrawer.Content className="flex flex-col gap-6">
            {fields}
            {rootError}
          </BottomDrawer.Content>
          <BottomDrawer.Footer>{action}</BottomDrawer.Footer>
        </BottomDrawer.Popup>
      </BottomDrawer.Root>
    </FormProvider>
  ) : (
    <Modal.Root
      open={open}
      onOpenChange={value => {
        if (!value) onClose()
      }}
    >
      <Modal.Popup
        className="mx-auto max-h-none w-full max-w-4xl! overflow-visible"
        viewportProps={{ className: 'block overflow-y-auto p-1' }}
        data-testid="update-post-modal"
      >
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <FormProvider {...formMethods}>
          <form
            onSubmit={formMethods.handleSubmit(onSubmit)}
            className="flex flex-col px-6 pb-6 lg:px-10 lg:pb-10"
          >
            {fields}
            <div className="flex justify-end">{action}</div>
            {rootError}
          </form>
        </FormProvider>
      </Modal.Popup>
    </Modal.Root>
  )
}
